/**
 * One slide, drawn per its layout (plan docs/plans/lms-plan.md 6.1, 7.2,
 * Appendix E; WP-40). Every markdown field goes through MdLite (block: the
 * *_md fields) or MdLiteInline (inline: bullets, table cells), every other
 * string is plain text, and a figure goes through Figure (D10: no raw HTML
 * anywhere, 18.11's imports).
 *
 * Layouts: title, bullets, two_column, callout, steps, table, figure, image,
 * scenario, knowledge_check, plus the player's own two (the change summary
 * first, the key takeaways last; deck.ts). speaker_notes render as a
 * collapsible "More" line (6.1): never knowledge the quiz depends on.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ImageOff, Lightbulb, ShieldCheck } from 'lucide-react';
import type {
  BulletsSlide,
  CalloutSlide,
  FigureSlide,
  ImageSlide,
  Slide,
  StepsSlide,
  TableSlide,
  TitleSlide,
  TwoColumnSlide,
} from '../../content/types';
import { MdLite, MdLiteInline } from '../../content/MdLite';
import { apiErrorMessage } from '../../api/client';
import { getAssetUrl } from '../../api/courses';
import type { SlideAnswer } from '../../hooks/usePlayer';
import { Figure } from './Figure';
import { KnowledgeCheck, Scenario } from './SlideQuestion';
import type { DeckSlide } from './deck';
import { bodyCls, headingCls, microLabelCls } from './styles';

export interface SlideViewProps {
  entry: DeckSlide;
  /** For the change-summary slide. */
  changeSummary?: string | null;
  versionNo?: number | null;
  keyTakeaways?: string[];
  answer?: SlideAnswer;
  onSelect?: (optionId: string, multi: boolean) => void;
  onCheck?: () => void;
  onChoose?: (optionId: string) => void;
}

function Heading({ children }: { children: string }) {
  return <h2 className={headingCls}>{children}</h2>;
}

function TitleLayout({ slide }: { slide: TitleSlide }) {
  return (
    <div className="space-y-4 py-4 sm:py-8">
      <h2 className="text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100 sm:text-xl">{slide.heading}</h2>
      <MdLite source={slide.body_md} size="base" />
    </div>
  );
}

function BulletsLayout({ slide }: { slide: BulletsSlide }) {
  const bullets = Array.isArray(slide.bullets) ? slide.bullets : [];
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <ul className={`${bodyCls} list-disc space-y-2 pl-5`}>
        {bullets.map((b, i) => (
          <li key={i}>
            <MdLiteInline source={b} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TwoColumnLayout({ slide }: { slide: TwoColumnSlide }) {
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <MdLite source={slide.left_md} size="base" />
        </div>
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <MdLite source={slide.right_md} size="base" />
        </div>
      </div>
    </div>
  );
}

const TONES: Record<CalloutSlide['tone'], { word: string; cls: string; Icon: typeof ShieldCheck }> = {
  // 6.2 rule 9: callouts mark must-do rules with the word Rule and consequences with the word Risk.
  rule: {
    word: 'Rule',
    cls: 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20',
    Icon: ShieldCheck,
  },
  risk: {
    word: 'Risk',
    cls: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
    Icon: AlertTriangle,
  },
  tip: {
    word: 'Tip',
    cls: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
    Icon: Lightbulb,
  },
};

function CalloutLayout({ slide }: { slide: CalloutSlide }) {
  const tone = TONES[slide.tone] ?? TONES.rule;
  const { Icon } = tone;
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <div className={`rounded-xl border p-4 ${tone.cls}`} data-testid="lms-callout" data-tone={slide.tone}>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{tone.word}</span>
        </div>
        <MdLite source={slide.body_md} size="base" />
      </div>
    </div>
  );
}

function StepsLayout({ slide }: { slide: StepsSlide }) {
  const steps = Array.isArray(slide.steps) ? slide.steps : [];
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              {i + 1}
            </span>
            <div className="min-w-0 space-y-1">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{step.title}</div>
              <MdLite source={step.body_md} size="base" />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TableLayout({ slide }: { slide: TableSlide }) {
  const columns = Array.isArray(slide.columns) ? slide.columns : [];
  const rows = Array.isArray(slide.rows) ? slide.rows : [];
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  <MdLiteInline source={c} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {rows.map((row, r) => (
              <tr key={r}>
                {(Array.isArray(row) ? row : []).map((cell, c) => (
                  <td key={c} className="px-4 py-3 align-top text-sm text-gray-900 dark:text-gray-100">
                    <MdLiteInline source={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FigureLayout({ slide }: { slide: FigureSlide }) {
  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <Figure figure={slide.figure} textEquivalent={slide.text_equivalent} title={slide.heading} />
    </div>
  );
}

/**
 * An image slide: the asset's presigned URL (GET /assets/:id/url, 5.2). Until
 * it answers, and if it fails, the alt text stands in, so the slide never
 * silently shows nothing (G10). Builtin content ships no images (D10).
 */
function ImageLayout({ slide }: { slide: ImageSlide }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    getAssetUrl(slide.asset_id)
      .then((out) => {
        if (!cancelled) setUrl(typeof out?.url === 'string' ? out.url : null);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The image could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [slide.asset_id]);

  return (
    <div className="space-y-4">
      <Heading>{slide.heading}</Heading>
      <figure className="space-y-2" data-testid="lms-image-slide">
        {url ? (
          <img src={url} alt={slide.alt} className="mx-auto max-h-[60vh] max-w-full rounded-lg" />
        ) : (
          <div
            role="img"
            aria-label={slide.alt}
            className="flex min-h-[8rem] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-600"
          >
            <ImageOff className="h-6 w-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <p className="text-sm text-gray-600 dark:text-gray-300">{slide.alt}</p>
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>
        )}
        {slide.caption && <figcaption className="text-xs text-gray-500 dark:text-gray-400">{slide.caption}</figcaption>}
      </figure>
    </div>
  );
}

function SpeakerNotes({ notes }: { notes: string }) {
  return (
    <details className="group mt-6 border-t border-gray-200 pt-3 dark:border-gray-700" data-testid="lms-speaker-notes">
      <summary className="inline-flex min-h-[44px] cursor-pointer items-center rounded-lg text-sm font-medium text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 sm:min-h-0">
        More
      </summary>
      <p className="mt-2 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{notes}</p>
    </details>
  );
}

function AuthoredSlide({ slide, answer, onSelect, onCheck, onChoose }: { slide: Slide } & Omit<SlideViewProps, 'entry'>) {
  switch (slide.layout) {
    case 'title':
      return <TitleLayout slide={slide} />;
    case 'bullets':
      return <BulletsLayout slide={slide} />;
    case 'two_column':
      return <TwoColumnLayout slide={slide} />;
    case 'callout':
      return <CalloutLayout slide={slide} />;
    case 'steps':
      return <StepsLayout slide={slide} />;
    case 'table':
      return <TableLayout slide={slide} />;
    case 'figure':
      return <FigureLayout slide={slide} />;
    case 'image':
      return <ImageLayout slide={slide} />;
    case 'scenario':
      return <Scenario slide={slide} answer={answer} onChoose={(id) => onChoose?.(id)} />;
    case 'knowledge_check':
      return (
        <KnowledgeCheck
          slide={slide}
          answer={answer}
          onSelect={(id) => onSelect?.(id, slide.kind === 'multi')}
          onCheck={() => onCheck?.()}
        />
      );
    default:
      return (
        <p className="text-sm text-red-600 dark:text-red-400">
          {`This slide has a layout the player does not know (${String((slide as { layout?: unknown }).layout)}).`}
        </p>
      );
  }
}

export function SlideView(props: SlideViewProps) {
  const { entry, changeSummary, versionNo, keyTakeaways } = props;

  if (entry.synthetic === 'change_summary') {
    return (
      <div className="space-y-4" data-testid="lms-slide-change-summary">
        <div className={microLabelCls}>{versionNo ? `Version ${versionNo}` : 'New version'}</div>
        <h2 className={headingCls}>What changed</h2>
        <p className={`${bodyCls} whitespace-pre-line`}>{changeSummary}</p>
      </div>
    );
  }

  if (entry.synthetic === 'takeaways') {
    return (
      <div className="space-y-4" data-testid="lms-slide-takeaways">
        <h2 className={headingCls}>Key takeaways</h2>
        <ul className={`${bodyCls} list-disc space-y-2 pl-5`}>
          {(keyTakeaways || []).map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>
    );
  }

  const slide = entry.slide as Slide;
  return (
    <div data-testid="lms-slide" data-layout={slide.layout} data-slide-id={slide.id}>
      <AuthoredSlide
        slide={slide}
        answer={props.answer}
        onSelect={props.onSelect}
        onCheck={props.onCheck}
        onChoose={props.onChoose}
      />
      {slide.speaker_notes ? <SpeakerNotes notes={slide.speaker_notes} /> : null}
    </div>
  );
}

export default SlideView;
